"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "OTA STRATEGY"
}, {
  id: "m-02",
  index: "02",
  label: "TECH STACK"
}, {
  id: "m-03",
  index: "03",
  label: "REVENUE AUDIT"
}, {
  id: "m-04",
  index: "04",
  label: "MARKET SHIFT"
}, {
  id: "m-05",
  index: "05",
  label: "DIRECT BOOKING"
}, {
  id: "m-06",
  index: "06",
  label: "PRICING MODEL"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "OTA STRATEGY",
  instructions: ["Optimize your property listings on Booking.com and Expedia with high-conversion photography.", "Implement dynamic rate parity to ensure your hotel remains competitive across all channels.", "Leverage OTA analytics to identify peak booking windows and adjust inventory accordingly.", "Use targeted promotions to capture high-intent travelers during low-demand periods.", "Monitor competitor pricing daily to maintain your position in the local market."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_diagram.png-wvc-sizes",
    alt: "Diagram showing OTA revenue optimization flow",
    tag: "[FIG. 01 / OTA FLOW]"
  },
  caution: "Avoid aggressive discounting that devalues your brand equity."
}, {
  id: "m-02",
  index: "M-02",
  title: "TECH STACK",
  instructions: ["Integrate a robust Property Management System (PMS) with your Channel Manager.", "Ensure your booking engine is mobile-responsive to capture on-the-go travelers.", "Utilize Revenue Management Systems (RMS) for automated, data-driven pricing decisions.", "Connect your CRM to personalize guest communication and drive loyalty.", "Regularly audit your tech stack to ensure seamless data flow between platforms."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_diagram.png-wvc-sizes",
    alt: "Diagram showing hotel technology integration",
    tag: "[FIG. 02 / TECH INTEGRATION]"
  },
  caution: "Incompatible software can lead to double bookings and data silos."
}, {
  id: "m-03",
  index: "M-03",
  title: "REVENUE AUDIT",
  instructions: ["Conduct a comprehensive audit of your current revenue streams and distribution costs.", "Analyze historical booking data to identify missed revenue opportunities.", "Review your current rate structure against local market benchmarks.", "Evaluate the performance of your current digital marketing and OTA spend.", "Identify operational bottlenecks that impact guest satisfaction and revenue."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_process.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_process.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_process.webp-wvc-sizes",
    alt: "Diagram of the revenue audit process",
    tag: "[FIG. 03 / AUDIT CYCLE]"
  },
  caution: "Regular audits are essential to prevent revenue leakage."
}, {
  id: "m-04",
  index: "M-04",
  title: "MARKET SHIFT",
  instructions: ["Monitor local tourism trends and seasonal demand fluctuations closely.", "Adjust your marketing strategy to target emerging traveler demographics.", "Pivot your inventory mix based on changing guest preferences and travel patterns.", "Stay informed about local events that impact hotel demand and pricing.", "Maintain agility in your operations to respond to sudden market changes."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_shift_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_shift_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_shift_analysis.png-wvc-sizes",
    alt: "Diagram of market shift analysis",
    tag: "[FIG. 04 / TREND MAP]"
  },
  caution: "Ignoring market shifts can lead to significant revenue loss."
}, {
  id: "m-05",
  index: "M-05",
  title: "DIRECT BOOKING",
  instructions: ["Enhance your website's user experience to simplify the booking process.", "Offer exclusive perks or loyalty discounts for guests who book directly.", "Implement SEO strategies to increase organic visibility for your property.", "Use retargeting ads to bring potential guests back to your booking engine.", "Ensure your website content is optimized for high conversion rates."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png-wvc-sizes",
    alt: "Diagram of direct booking growth strategy",
    tag: "[FIG. 05 / CONVERSION FUNNEL]"
  },
  caution: "High OTA dependency reduces your profit margins significantly."
}, {
  id: "m-06",
  index: "M-06",
  title: "PRICING MODEL",
  instructions: ["Adopt a dynamic pricing model that responds to real-time demand signals.", "Segment your pricing based on room types, guest profiles, and booking lead times.", "Use competitive intelligence to inform your pricing strategy.", "Implement length-of-stay restrictions during high-demand periods.", "Regularly review and refine your pricing strategy based on performance data."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pricing_model_chart.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pricing_model_chart.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pricing_model_chart.webp-wvc-sizes",
    alt: "Chart of dynamic pricing model",
    tag: "[FIG. 06 / PRICING MATRIX]"
  },
  caution: "Static pricing is a major barrier to revenue maximization."
}];
export default function ResortStrategyFaq() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="resortstrategyfaq" className="bg-background text-foreground py-28 md:py-40">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-500 ease-out ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block size-2.5 bg-secondary" aria-hidden="true" />
            <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              § 01 — Revenue Strategy FAQ
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
            <h1 className="font-default font-black uppercase leading-[0.88] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 8.2vw, 7.5rem)"
          }}>
              The{" "}
              <em className="not-italic md:italic font-black" style={{
              color: "var(--secondary)",
              fontStyle: "italic"
            }}>
                Expert's
              </em>{" "}
              Guide To Hotel Revenue.
            </h1>
            <div className="shrink-0">
              <span className="inline-block font-mono text-xs font-semibold uppercase tracking-[0.12em] text-foreground border border-foreground px-3 py-1.5">
                [REV. 2025.03 / 24 PAGES]
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px)",
        backgroundColor: "var(--background)",
        opacity: 0.85
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-mono text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border whitespace-nowrap transition-colors ${activeId === item.id ? "border-secondary bg-secondary text-secondary-foreground" : "border-foreground/40 text-foreground hover:border-foreground"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-secondary" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-secondary pl-4" : "border-transparent pl-2 hover:border-foreground/40 hover:pl-3"}`}>
                        <span className="font-mono text-xs font-semibold tracking-[0.12em] text-muted-foreground shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-default font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-foreground last:border-b last:border-foreground data-[state=open]:border-l-4 data-[state=open]:border-l-secondary transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        [{entry.index}]
                      </span>
                      <span className="font-default font-bold uppercase tracking-tight leading-none text-left text-foreground" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-secondary text-secondary-foreground justify-self-end shrink-0">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-card p-6 md:p-8 border border-foreground/15">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-secondary" aria-hidden="true" />
                            Strategy
                          </div>
                          <ol className="space-y-3 font-default text-base leading-relaxed text-foreground">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-mono text-xs font-semibold tracking-[0.1em] text-muted-foreground pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-foreground/15">
                              <p className="font-mono text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-foreground">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "var(--secondary)"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative">
                            <span className="absolute top-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-foreground/30" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              {entry.figure.tag}
                            </span>
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}